import { _ } from "../../../../core/polyglot/polyglot.js";
import {
	notBlank,
	required,
	validateAll,
	validInteger,
	validNumber,
} from "../../../../core/widgets/forms/validators.js";
import AbstractFormulaTokenVisitor from "../../../../models/keyFigure/formulaToken/visitors/AbstractFormulaTokenVisitor.js";
import FormBuilder from "../../../../core/widgets/forms/FormBuilder.js";
import KeyFigureDependencyChecker from "../../../../models/keyFigure/formula/visitors/KeyFigureDependencyChecker.js";
import numbro from "../../../../core/numbroWrapper.js";

/**
 * Visitor used to produce forms to edit formula token.
 *
 * @param {keyFigure[]} spec.keyFigures - List of keyFigures that can be used in tokens.
 * @param {Period} spec.period - Current period.
 * @param {chartOfAccounts} [spec.chartOfAccounts] - COA for the account groups
 * @param {string} editedKeyFigureId - Unique identifier of the
 *   keyFigure being edited. This is used to be sure the user doesn't
 *   add a keyFigure depending on the current one.
 */
export default class FormulaTokenFormBuilder extends AbstractFormulaTokenVisitor {
	constructor({ keyFigures, period, chartOfAccounts, editedKeyFigureId }) {
		super(...arguments);
		this._keyFigures = keyFigures;
		this._period = period;
		this._chartOfAccounts = chartOfAccounts;
		this._editedKeyFigureId = editedKeyFigureId;
	}

	visitAbstractFormulaToken(token) {
		return new FormBuilder({
			model: token,
			showActions: false,
		});
	}

	visitAccountFormulaToken(token) {
		const form = super.visitAccountFormulaToken(token);

		form.newDropDownField({
			label: _("Account"),
			name: "account",
			accessor: token.getAccountNumber.bind(token),
			mutator: token.setAccountNumber.bind(token),
			items: this._period.getAccounts().map((account) => ({
				label: account.toString(),
				value: account.getNumber(),
			})),
		});

		return form;
	}

	visitAccountGroupFormulaToken(token) {
		const form = super.visitAccountGroupFormulaToken(token);
		const accountGroups = this._chartOfAccounts.getAllGroups();

		form.tree({
			label: _("Account group"),
			autoOpen: 0,
			accessor: () =>
				accountGroups.find(
					(group) => group.getId() === token.getGroupId(),
				),
			mutator: (group) => token.setGroup(group),
			data: this._chartOfAccounts.getMainTree().getChildren(),
			getChildren: (item) =>
				item.getChildren().filter((child) => child.isAccountGroup()),
			getLabel: (item) => item.getName(),
			validator: required(),
		});

		return form;
	}

	visitAccountIntervalFormulaToken(token) {
		const form = super.visitAccountIntervalFormulaToken(token);

		form.input({
			label: _("Start"),
			name: "interval-start",
			placeHolder: "1001",
			accessor: token.getStart.bind(token),
			mutator: token.setStart.bind(token),
			validator: validateAll(validInteger(), notBlank()),
		});

		form.input({
			label: _("End"),
			name: "interval-end",
			placeHolder: "1999",
			accessor: token.getEnd.bind(token),
			mutator: token.setEnd.bind(token),
			validator: validateAll(validInteger(), notBlank()),
		});

		return form;
	}

	visitConstantFormulaToken(token) {
		const form = super.visitConstantFormulaToken(token);

		form.input({
			label: _("Value"),
			name: "constant",
			accessor: token.getValue.bind(token),
			mutator: (value) => token.setValue(numbro.unformat(value)),
			validator: validateAll(validNumber(), notBlank()),
		});

		return form;
	}

	visitKeyFigureFormulaToken(token) {
		const form = super.visitKeyFigureFormulaToken(token);

		form.keyFigurePickerField({
			keyFigures: this._keyFigures,
			accessor: () => token.getKeyFigure(),
			mutator: (keyFigure) => token.setKeyFigure(keyFigure),
			checkIfEnabled: (keyFigure) => {
				const { isFormulaValid, hasDependency } =
					KeyFigureDependencyChecker.checkDependency({
						keyFigure,
						dependencyId: this._editedKeyFigureId,
					});

				if (!isFormulaValid) {
					return {
						isEnabled: false,
						disabledTitle: _("The key figure formula is not valid"),
					};
				}

				return {
					isEnabled: !hasDependency,
					disabledTitle: _(
						"You cannot select a key figure depending on the current one",
					),
				};
			},
		});

		return form;
	}
}
